import { Injectable, signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { signOut } from 'firebase/auth';

import { State, StorageState } from '../models/state.model';
import { FirebaseService } from './firebase.service';

const initialState: State = {
  authUser: null,
  currentUser: null,
  currentAccount: null,
  currentOrganization: null,
  currentMember: null,
  currentRole: null,
};

@Injectable({
  providedIn: 'root',
})
export class DataStoreService {
  private readonly firebase = inject(FirebaseService);
  private readonly router = inject(Router);

  private readonly _state = signal<State>({ ...initialState });

  /** Observable for subscription-based consumers */
  readonly state$ = toObservable(this._state);

  /** Computed read-only accessors */
  readonly authUser = computed(() => this._state().authUser);
  readonly currentUser = computed(() => this._state().currentUser);
  readonly currentAccount = computed(() => this._state().currentAccount);
  readonly currentOrganization = computed(() => this._state().currentOrganization);
  readonly currentMember = computed(() => this._state().currentMember);
  readonly currentRole = computed(() => this._state().currentRole);

  private authState: StorageState = {};
  private sessionState: StorageState = {};

  constructor() {
    let loggedIn = false;
    this.firebase.auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        if (authUser.isAnonymous || authUser.email === null) {
          this.clearState();
          this.clearAuthState();
          signOut(this.firebase.auth);
          return;
        }

        this.updateState({ authUser });
        loggedIn = true;
      } else {
        this.clearState();
        this.clearAuthState();
        if (loggedIn) {
          loggedIn = false;
          this.router.navigate(['/login']);
        }
      }
    });
  }

  getProperty<T extends keyof State>(property: T): State[T] {
    return this._state()[property];
  }

  updateState(newState: Partial<State>) {
    this._state.update((current) => ({ ...current, ...newState }));
  }

  clearState() {
    this._state.set({ ...initialState });
  }

  // -- Session storage --

  setSessionStateProperty(key: string, value: any) {
    this.sessionState[key] = true;
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getSessionStateProperty(key: string) {
    return JSON.parse(sessionStorage.getItem(key) || 'null');
  }

  removeSessionStateProperty(key: string) {
    delete this.sessionState[key];
    sessionStorage.removeItem(key);
  }

  // -- Auth (localStorage) storage --

  setAuthStateProperty(key: string, value: any, stringify = true) {
    this.authState[key] = true;
    const data = stringify ? JSON.stringify(value) : value;
    localStorage.setItem(key, data);
  }

  getAuthStateProperty(key: string, parse = true) {
    const data = localStorage.getItem(key);
    return parse ? JSON.parse(data || 'null') : data;
  }

  removeAuthStateProperty(key: string) {
    delete this.authState[key];
    localStorage.removeItem(key);
  }

  clearAuthState() {
    for (const key in this.authState) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem('currentAccount');
    this.authState = {};
  }
}
